import { Shield, FileText, Lock, Eye, Users, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy – PlaySpiro</title>
        <meta name="description" content="Read the PlaySpiro privacy policy to understand how we collect and protect your personal data." />
        <link rel="canonical" href="https://playspiro.com/privacy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="privacy-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Privacy <span className="text-primary">Policy</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Last updated: January 2026
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4">
        <div className="glass rounded-2xl p-6 md:p-8 space-y-8">
          {/* Introduction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Introduction</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              PlaySpiro ("we", "our", or "us") is committed to protecting your privacy. This Privacy 
              Policy explains how we collect, use, disclose, and safeguard your information when you 
              visit our website playspiro.com. Please read this privacy policy carefully. If you do 
              not agree with the terms of this privacy policy, please do not access the site.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-secondary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-white/70">
              <p><strong className="text-white">Personal Data:</strong> When you submit a complaint, 
              contact us, or subscribe to our newsletter, we may collect personally identifiable 
              information such as your name, email address, and any other information you provide.</p>
              
              <p><strong className="text-white">Usage Data:</strong> We automatically collect certain 
              information when you visit our website, including your IP address, browser type, operating 
              system, referring URLs, pages viewed, and the dates and times of your visits.</p>
              
              <p><strong className="text-white">Cookies:</strong> We use cookies and similar tracking 
              technologies to track activity on our website and store certain information. You can 
              instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</p>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-green-400" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">How We Use Your Information</h2>
            </div>
            <ul className="space-y-2 text-white/70">
              <li>• To provide and maintain our website</li>
              <li>• To process and resolve complaints</li>
              <li>• To send newsletters and marketing communications (with your consent)</li>
              <li>• To respond to your inquiries and support requests</li>
              <li>• To analyze usage patterns and improve our services</li>
              <li>• To detect, prevent, and address technical issues</li>
              <li>• To comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-orange-400" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Data Sharing</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information in the following situations:
            </p>
            <ul className="space-y-2 text-white/70">
              <li>• With casinos to resolve complaints (with your consent)</li>
              <li>• With service providers who assist in operating our website</li>
              <li>• To comply with legal obligations or protect our rights</li>
              <li>• In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Data Security</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information. However, no method of transmission over the Internet or electronic 
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">Your Rights</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="space-y-2 text-white/70">
              <li>• Right to access your personal data</li>
              <li>• Right to rectify inaccurate data</li>
              <li>• Right to erasure ("right to be forgotten")</li>
              <li>• Right to restrict processing</li>
              <li>• Right to data portability</li>
              <li>• Right to object to processing</li>
              <li>• Right to withdraw consent</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="border-t border-white/10 pt-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-secondary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Contact Us</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:
              <br /><br />
              <strong className="text-white">Email:</strong>{" "}
              <a href="mailto:support@playspiro.com" className="text-primary hover:underline">
                support@playspiro.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
      </>
  );
}